import React, { useEffect, useState } from 'react';
import { QuestionnaireTemplate, Question, QuestionType, Service, AddOn } from '@/types';
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  X,
  MessageCircle,
  PlusCircle,
  Loader2,
  Search
} from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { createId } from '@/lib/ids';
import { api } from '@/services/api';
import { Pagination } from '@shared/components/common/Pagination';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface QuestionnairesTabProps {
  services: Service[];
  addOns: AddOn[];
  onAddQuestionnaire: (q: QuestionnaireTemplate) => void;
  onUpdateQuestionnaire: (q: QuestionnaireTemplate) => void;
  onDeleteQuestionnaire: (id: string) => void;
  canEdit: boolean;
}

interface QuestionEditorProps {
  question: Question;
  onChange: (q: Question) => void;
  onDelete: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, onChange, onDelete }) => {
  const isQuestionType = (value: string): value is QuestionType =>
    value === 'text' || value === 'textarea' || value === 'yes_no' || value === 'select';

  const handleTypeChange = (value: string) => {
    if (!isQuestionType(value)) return;
    onChange({ ...question, type: value });
  };

  const handleTriggerChange = (val: 'yes' | 'no' | 'none') => {
    const updates: Partial<Question> = { requireDetailsOn: val };

    // Auto-populate logic: if user selects 'yes' and prompt is empty, add default text
    if (val === 'yes' && (!question.detailsLabel || question.detailsLabel.trim() === '')) {
      updates.detailsLabel = 'please provide more information';
    }

    onChange({ ...question, ...updates });
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-sm relative group/editor">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 mr-2">
          <input
            className="w-full border-2 border-slate-100 rounded-lg p-2 text-sm bg-white font-bold outline-none focus:border-teal-500 transition-colors"
            placeholder="Question Label (e.g. Do you have heart conditions?)"
            value={question.label}
            onChange={(e) => onChange({ ...question, label: e.target.value })}
          />
        </div>
        <button
          onClick={onDelete}
          className="text-slate-300 hover:text-red-500 p-2 transition-colors"
          title="Delete Question"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
              Response Type
            </label>
            <select
              className="w-full border rounded-lg p-2 text-xs bg-white font-medium outline-none"
              value={question.type}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              <option value="text">Short Text</option>
              <option value="textarea">Long Text / Details</option>
              <option value="yes_no">Yes / No Toggle</option>
              <option value="select">Dropdown Selection</option>
            </select>
          </div>
          <div className="pt-5">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-teal-600 rounded"
                checked={question.required}
                onChange={(e) => onChange({ ...question, required: e.target.checked })}
              />
              Mandatory
            </label>
          </div>
        </div>

        {/* Conditional follow-up for Yes/No */}
        {question.type === 'yes_no' && (
          <div className="bg-teal-50/50 p-3 rounded-lg border border-teal-100">
            <p className="text-[10px] font-bold text-teal-700 uppercase mb-2 flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> Follow-up Logic
            </p>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-[9px] font-bold text-teal-600 uppercase mb-1">
                  Require details if:
                </label>
                <select
                  className="w-full border rounded p-1 text-[11px] bg-white"
                  value={question.requireDetailsOn || 'none'}
                  onChange={(e) => handleTriggerChange(e.target.value as 'yes' | 'no' | 'none')}
                >
                  <option value="none">Disabled</option>
                  <option value="yes">If patient answers 'Yes'</option>
                  <option value="no">If patient answers 'No'</option>
                </select>
              </div>
              {question.requireDetailsOn && question.requireDetailsOn !== 'none' && (
                <div className="flex-[2]">
                  <label className="block text-[9px] font-bold text-teal-600 uppercase mb-1">
                    Details Prompt
                  </label>
                  <input
                    className="w-full border rounded p-1 text-[11px] bg-white"
                    placeholder="e.g. Please provide dates..."
                    value={question.detailsLabel || ''}
                    onChange={(e) => onChange({ ...question, detailsLabel: e.target.value })}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {question.type === 'select' && (
        <div className="mt-4">
          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
            Options (comma separated)
          </label>
          <input
            className="w-full border rounded-lg p-2 text-xs bg-white"
            value={question.options?.join(', ') || ''}
            onChange={(e) =>
              onChange({
                ...question,
                options: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              })
            }
            placeholder="Option 1, Option 2, Option 3"
          />
        </div>
      )}
    </div>
  );
};

export const QuestionnairesTab: React.FC<QuestionnairesTabProps> = ({
  services,
  addOns,
  onAddQuestionnaire,
  onUpdateQuestionnaire,
  onDeleteQuestionnaire,
  canEdit
}) => {
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireTemplate[]>([]);
  const [isLoadingQuestionnaires, setIsLoadingQuestionnaires] = useState(false);
  const [questionnairesError, setQuestionnairesError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<QuestionnaireTemplate | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingQuestionnaires(true);
    setQuestionnairesError('');
    api
      .listStaffQuestionnaires({ page, pageSize, q: search || undefined })
      .then((data) => {
        if (!isActive) return;
        setQuestionnaires(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setQuestionnaires([]);
        setTotalCount(0);
        setQuestionnairesError(normalizeErrorMessage(err, 'Failed to load questionnaires.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingQuestionnaires(false);
      });

    return () => {
      isActive = false;
    };
  }, [page, pageSize, search]);

  const refreshQuestionnaires = async () => {
    setIsLoadingQuestionnaires(true);
    setQuestionnairesError('');
    try {
      const data = await api.listStaffQuestionnaires({ page, pageSize, q: search || undefined });
      setQuestionnaires(data.items || []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setQuestionnaires([]);
      setTotalCount(0);
      setQuestionnairesError(normalizeErrorMessage(err, 'Failed to load questionnaires.'));
    } finally {
      setIsLoadingQuestionnaires(false);
    }
  };

  const addNewQuestion = (index?: number) => {
    if (!editingTemplate) return;
    const newQuestion: Question = {
      id: createId(),
      type: 'yes_no',
      label: '',
      required: true,
      requireDetailsOn: 'none'
    };

    const newQuestions = [...editingTemplate.questions];
    if (typeof index === 'number') {
      newQuestions.splice(index + 1, 0, newQuestion);
    } else {
      newQuestions.push(newQuestion);
    }

    setEditingTemplate({
      ...editingTemplate,
      questions: newQuestions
    });
  };

  const handleDeleteRequest = async (template: QuestionnaireTemplate) => {
    // Check usage in services
    const usage = services.filter((s) => s.questionnaireConfig?.templateId === template.id);
    const addOnUsage = addOns.filter((a) => a.questionnaireConfig?.templateId === template.id);

    if (usage.length > 0 || addOnUsage.length > 0) {
      const serviceNames = usage.map((s) => s.name).join(', ');
      const addOnNames = addOnUsage.map((a) => a.name).join(', ');
      const usageParts = [];
      if (serviceNames) usageParts.push(`service(s): ${serviceNames}`);
      if (addOnNames) usageParts.push(`add-on(s): ${addOnNames}`);
      showAlertModal(
        `Cannot delete: This form is currently assigned to ${usageParts.join(
          ' and '
        )}. Please unassign it before deleting.`
      );
      return;
    }

    const confirmed = await showConfirmModal(
      `Are you sure you want to delete the "${template.name}" questionnaire? This will permanently remove the template.`,
      {
        title: 'Delete Questionnaire',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDestructive: true
      }
    );
    if (!confirmed) return;

    await Promise.resolve(onDeleteQuestionnaire(template.id));
    await refreshQuestionnaires();
  };

  return (
    <div className="animate-fade-in">
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-scale-up border border-slate-200">
            <div className="p-6 border-b flex justify-between items-center bg-slate-50 rounded-t-3xl">
              <div>
                <h3 className="font-black text-xl text-slate-900">
                  {editingTemplate.id ? 'Refine Questionnaire' : 'Build New Questionnaire'}
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  Design clinical questions and data points.
                </p>
              </div>
              <button
                onClick={() => setEditingTemplate(null)}
                className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-8 flex-1 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                    Form Name
                  </label>
                  <input
                    className="w-full border-2 border-slate-100 p-3 rounded-xl bg-white outline-none focus:border-teal-500 font-bold"
                    value={editingTemplate.name}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, name: e.target.value })
                    }
                    placeholder="e.g. Safety Critical Core"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                    Internal Description
                  </label>
                  <input
                    className="w-full border-2 border-slate-100 p-3 rounded-xl bg-white outline-none focus:border-teal-500 font-medium"
                    value={editingTemplate.description || ''}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, description: e.target.value })
                    }
                    placeholder="What is this form for?"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-black text-sm text-slate-700 uppercase tracking-widest flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-slate-300" /> Form Structure
                  </h4>
                  {editingTemplate.questions.length === 0 && (
                    <button
                      onClick={() => addNewQuestion()}
                      className="bg-slate-900 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Add First Question
                    </button>
                  )}
                </div>

                {editingTemplate.questions.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl">
                    <p className="text-sm text-slate-400 italic">
                      No questions added yet. Click the button above to start.
                    </p>
                  </div>
                ) : (
                  editingTemplate.questions.map((q, idx) => (
                    <React.Fragment key={q.id}>
                      <QuestionEditor
                        question={q}
                        onChange={(updated) => {
                          const newQs = [...editingTemplate.questions];
                          newQs[idx] = updated;
                          setEditingTemplate({ ...editingTemplate, questions: newQs });
                        }}
                        onDelete={() => {
                          setEditingTemplate({
                            ...editingTemplate,
                            questions: editingTemplate.questions.filter((_, i) => i !== idx)
                          });
                        }}
                      />
                      <div className="flex justify-center -my-2 relative z-10 group/add">
                        <button
                          onClick={() => addNewQuestion(idx)}
                          className="bg-white border-2 border-slate-100 text-slate-400 hover:text-teal-600 hover:border-teal-100 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Insert Question Below
                        </button>
                      </div>
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3 bg-slate-50 rounded-b-3xl">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-6 py-3 text-slate-500 font-bold hover:bg-white rounded-xl transition-all"
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  if (editingTemplate.id) {
                    await Promise.resolve(onUpdateQuestionnaire(editingTemplate));
                  } else {
                    await Promise.resolve(
                      onAddQuestionnaire({
                        ...editingTemplate,
                        id: createId()
                      })
                    );
                  }
                  setEditingTemplate(null);
                  await refreshQuestionnaires();
                }}
                className="bg-teal-600 text-white px-10 py-3 rounded-xl font-black hover:bg-teal-700 transition-all shadow-xl shadow-teal-600/20 active:scale-95"
              >
                Save Questionnaire
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <div>
          <h3 className="text-2xl font-black text-slate-800">Questionnaires</h3>
          <p className="text-sm text-slate-500">
            Create smart medical forms with conditional follow-ups.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search forms..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-56 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() => setEditingTemplate({ id: '', name: '', questions: [] })}
              className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 hover:bg-slate-800 shadow-xl transition-all active:scale-95"
            >
              <Plus className="w-5 h-5" /> New Template
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoadingQuestionnaires ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
            Loading questionnaires...
          </div>
        ) : questionnairesError ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-red-500">
            {questionnairesError}
          </div>
        ) : questionnaires.length === 0 ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
            No questionnaires found.
          </div>
        ) : (
          questionnaires.map((q) => {
            const isUsed =
              services.some((s) => s.questionnaireConfig?.templateId === q.id) ||
              addOns.some((a) => a.questionnaireConfig?.templateId === q.id);
            return (
              <div
                key={q.id}
                className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm relative group hover:shadow-xl transition-all flex flex-col justify-between"
              >
                {canEdit && (
                  <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingTemplate(q)}
                      className="p-2 bg-white/90 backdrop-blur rounded-full shadow-sm text-blue-600 hover:bg-blue-50 border border-slate-100"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRequest(q)}
                      className="p-2 bg-white/90 backdrop-blur rounded-full shadow-sm text-red-600 hover:bg-red-50 border border-slate-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div>
                  <h4 className="font-black text-xl text-slate-900 mb-2 leading-tight">{q.name}</h4>
                  <p className="text-slate-500 text-xs mb-4 line-clamp-2 font-medium">
                    {q.description || 'Standard assessment form.'}
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-teal-100">
                      {q.questions.length} Fields
                    </span>
                    {isUsed && (
                      <span
                        className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100"
                        title="This form is active in Service or Add-on config"
                      >
                        Active
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-bold text-slate-300 uppercase tracking-tighter">
                    ID: {q.id}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[25, 50, 100]}
        isLoading={isLoadingQuestionnaires}
      />
    </div>
  );
};
